/**
 * Viewport-driven motion enhancement. Each content unit is observed directly,
 * so section padding cannot complete an entrance before content is visible.
 * Content stays visible when this file never runs (the watchdog in
 * snippets/css-variables.liquid) or when IntersectionObserver is unavailable.
 */
// FIRST, so the head watchdog knows this module ran. See css-variables.liquid.
/** @type {any} */ (window).__yuuthMotion = true;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/** @type {WeakSet<Element>} */
const observedTargets = new WeakSet();
/** @type {Set<IntersectionObserver>} */
const activeObservers = new Set();

/**
 * The wordmark IS in this list, deliberately, and it was briefly removed by
 * mistake on 2026-08-06. Recording that here so it is not removed again.
 *
 * It was reported as "the footer logo animating its gradient, contained inside
 * SVG bound". That reading is understandable: the wordmark is a full-bleed SVG
 * sitting directly on the footer gradient, so fading and translating it
 * lightens and shifts the whole panel within the SVG's own box. But the
 * gradient itself has never animated, and the entrance is wanted. Joey
 * confirmed: restore it exactly as it was, fade plus slide.
 *
 * It only shows on machines without `prefers-reduced-motion: reduce`, which is
 * why it is easy to believe it is absent when it is not.
 *
 * @param {Document | Element} root
 */
function prepareFooterTargets(root = document) {
  root.querySelectorAll('.site-footer__wordmark, .site-footer__legal-row, .site-footer__legal-copy').forEach((target) => {
    target.setAttribute('data-motion-reveal', '');
  });
}

/** @param {Document | Element} root */
function targetsWithin(root = document) {
  const targets = Array.from(root.querySelectorAll('[data-motion-reveal]'));
  if (root instanceof Element && root.matches('[data-motion-reveal]')) targets.unshift(root);
  return targets;
}

/** @param {Element} target */
function reveal(target) {
  target.classList.add('is-revealed');
}

/** @param {Document | Element} root */
function revealAll(root = document) {
  targetsWithin(root).forEach(reveal);
}

/** @param {Document | Element} root */
function enableReveals(root = document) {
  prepareFooterTargets(root);
  const targets = targetsWithin(root).filter((target) => !observedTargets.has(target) && !target.classList.contains('is-revealed'));
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    disableMotion();
    return;
  }

  if (reducedMotion.matches) {
    targets.forEach(reveal);
    return;
  }

  const pendingTargets = new Set(targets);
  /** @type {IntersectionObserver} */
  let observer;

  /**
   * Reveal a target, and if it belongs to a group, reveal the whole group in
   * DOM order.
   *
   * WHY GROUPS: stagger used to be a per-element `transition-delay`, but every
   * element was observed individually, so a delay meant "this many ms after
   * THIS element crossed the threshold" rather than "after the first one did".
   * For a vertical stack the members cross at different times and then each
   * adds its own delay on top, so on a fast scroll a lower item with a smaller
   * delay could reveal BEFORE a higher item with a larger one. The order was
   * literally scroll-speed dependent.
   *
   * Now the first member to cross reveals the whole group, and each member's
   * index drives the delay. Order is deterministic regardless of scroll speed.
   *
   * @param {Element} target
   */
  const settleGroup = (target) => {
    const group = target.closest('[data-motion-group]');
    // The group element can ALSO be a reveal target — four grids carry both
    // attributes. `querySelectorAll` only returns descendants, so the group
    // itself was never revealed and stayed at its opacity: 0 base state: every
    // product, collection and search grid rendered invisible while its own
    // children were correctly revealed inside it. Include the group when it is
    // a target in its own right.
    const members = group
      ? Array.from(group.querySelectorAll('[data-motion-reveal]'))
      : [target];
    if (group && group.hasAttribute('data-motion-reveal') && !members.includes(group)) {
      members.unshift(group);
    }

    members.forEach((member, index) => {
      if (member instanceof HTMLElement) member.style.setProperty('--motion-index', String(index));
      reveal(member);
      if (!pendingTargets.delete(member)) return;
      observer.unobserve(member);
    });

    if (pendingTargets.size) return;
    observer.disconnect();
    activeObservers.delete(observer);
  };

  /** @param {Element} target */
  const settle = (target) => {
    settleGroup(target);
  };

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      settle(entry.target);
    });
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });

  activeObservers.add(observer);
  targets.forEach((target) => observedTargets.add(target));
  document.documentElement.classList.add('motion-ready');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      targets.forEach((target) => observer.observe(target));

      // Fail open if a browser restores or rapidly scrolls to a target without
      // delivering the expected observer notification.
      window.setTimeout(() => {
        targets.forEach((target) => {
          if (target.classList.contains('is-revealed')) return;
          const bounds = target.getBoundingClientRect();
          if (bounds.bottom > 0 && bounds.top < window.innerHeight * 0.96) {
            settle(target);
          }
        });
      }, 800);
    });
  });
}

function disableMotion() {
  activeObservers.forEach((observer) => observer.disconnect());
  activeObservers.clear();
  revealAll();
  document.documentElement.classList.remove('motion-ready');
}

enableReveals();

document.addEventListener('shopify:section:load', (event) => enableReveals(/** @type {Element} */ (event.target)));
window.addEventListener('pageshow', () => enableReveals());
reducedMotion.addEventListener('change', (event) => {
  if (event.matches) disableMotion();
});

export {};
